import { useState, useEffect } from "react";
import { CheckCircle, XCircle, Clock, ExternalLink, Copy, RotateCcw, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

interface TransactionStatusIndicatorProps {
  status: "pending" | "confirmed" | "failed";
  signature?: string;
  confirmations?: number;
  maxConfirmations?: number;
  estimatedTimeRemaining?: number; // in seconds
  explorerUrl?: string;
  error?: string;
  onRetry?: () => void;
  className?: string;
}

export function TransactionStatusIndicator({
  status,
  signature,
  confirmations = 0,
  maxConfirmations = 32,
  estimatedTimeRemaining,
  explorerUrl,
  error,
  onRetry,
  className,
}: TransactionStatusIndicatorProps) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast({ title: "Copied to clipboard", description: "Transaction signature copied successfully" });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({ title: "Copy failed", description: "Failed to copy to clipboard", variant: "destructive" });
    }
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getExplorerUrl = (signature: string, network: "devnet" | "mainnet" = "devnet") => {
    return `https://explorer.solana.com/tx/${signature}?cluster=${network}`;
  };

  const getErrorHelp = (error: string) => {
    const lowerError = error.toLowerCase();
    if (lowerError.includes("insufficient")) {
      return "Ensure you have enough SOL in your wallet to cover transaction fees and any token purchases.";
    }
    if (lowerError.includes("timeout") || lowerError.includes("rpc")) {
      return "Network congestion may be causing delays. Try again in a few moments.";
    }
    if (lowerError.includes("user rejected") || lowerError.includes("cancel")) {
      return "Transaction was cancelled. You can retry when ready.";
    }
    if (lowerError.includes("simulation")) {
      return "Transaction simulation failed. Check your wallet balance and try again.";
    }
    return "An unexpected error occurred. Please try again or contact support if the issue persists.";
  };

  return (
    <div className={cn(
      "p-4 rounded-xl border",
      status === "pending" ? "bg-warning/5 border-warning/20" :
      status === "confirmed" ? "bg-success/5 border-success/20" :
      "bg-destructive/5 border-destructive/20",
      className
    )}>
      {/* Status Header */}
      <div className="flex items-center gap-3 mb-3">
        {status === "pending" && <Clock className="w-5 h-5 text-warning animate-pulse" />}
        {status === "confirmed" && <CheckCircle className="w-5 h-5 text-success" />}
        {status === "failed" && <XCircle className="w-5 h-5 text-destructive" />}

        <div className="flex-1">
          <h3 className="font-semibold text-sm">
            {status === "pending" && "Transaction Pending"}
            {status === "confirmed" && "Transaction Confirmed"}
            {status === "failed" && "Transaction Failed"}
          </h3>
          {status === "pending" && estimatedTimeRemaining && (
            <p className="text-xs text-muted-foreground">
              Estimated time: {formatTime(estimatedTimeRemaining)}
            </p>
          )}
        </div>

        {status === "failed" && onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="flex items-center gap-1"
          >
            <RotateCcw className="w-3 h-3" />
            Retry
          </Button>
        )}
      </div>

      {/* Confirmation Progress */}
      {status === "pending" && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>Confirmations</span>
            <span>{confirmations}/{maxConfirmations}</span>
          </div>
          <div className="w-full bg-secondary rounded-full h-2">
            <div
              className="bg-warning h-2 rounded-full transition-all duration-300"
              style={{ width: `${(confirmations / maxConfirmations) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Transaction Signature */}
      {signature && (
        <div className="mb-3">
          <p className="text-xs text-muted-foreground mb-1">Transaction Signature</p>
          <div className="flex items-center gap-2 p-2 bg-secondary/50 rounded-lg">
            <code className="text-xs font-mono flex-1 break-all">{signature}</code>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => copyToClipboard(signature)}
              className="flex-shrink-0"
            >
              <Copy className={cn("w-3 h-3", copied && "text-success")} />
            </Button>
          </div>
        </div>
      )}

      {/* Error Details */}
      {status === "failed" && error && (
        <div className="mb-3 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs font-medium text-destructive mb-1">Error Details</p>
              <p className="text-xs text-destructive/80 font-mono mb-2">{error}</p>
              <p className="text-xs text-muted-foreground">{getErrorHelp(error)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Explorer Links */}
      {signature && (
        <div className="flex gap-2">
          <a
            href={getExplorerUrl(signature, "devnet")}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <ExternalLink className="w-3 h-3" />
            View on Devnet Explorer
          </a>
          <a
            href={getExplorerUrl(signature, "mainnet")}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <ExternalLink className="w-3 h-3" />
            View on Mainnet Explorer
          </a>
        </div>
      )}
    </div>
  );
}